/**
 * What the desk understood from an RFQ intake, laid out for a person to check:
 * customer, due date, lines and quantities, each with where it came from, plus
 * the gaps the engine flagged and a customer question drafted from them.
 *
 * Pure: everything here is derived from the RFQ record, so it is testable
 * without a network and the page stays thin.
 */
import type { IntakeFinding, IntakeSource, RfqLine, RfqRecord } from "./rfqApi";

export type Locale = "en" | "tr";

export type Provenance =
  | { kind: "mail" }
  | { kind: "file"; name: string }
  | { kind: "drawing"; name: string }
  | { kind: "entered" }
  | { kind: "none" };

export interface HeaderRow {
  key: "customer" | "email" | "due" | "lines";
  value: string | null;
  provenance: Provenance;
}

export interface LineRow {
  line: RfqLine;
  qtyKnown: boolean;
  provenance: Provenance;
}

const COPY = {
  en: {
    customer: "Customer name",
    email: "Customer e-mail",
    due: "Due date",
    lines: "Items",
    fromMail: "from the mail",
    fromFile: (n: string) => `from ${n}`,
    fromDrawing: (n: string) => (n.trim() && n.trim() !== "drawing" ? `from drawing ${n.trim()}` : "from drawing"),
    entered: "entered by you",
    none: "not found",
    assumed: "assumed",
    subject: (no: string) => `${no} - clarification`,
    greeting: (name: string | null) => (name ? `Dear ${name},` : "Hello,"),
    intro: "Thank you for your request. To prepare an accurate quote we need a few details:",
    qty: (part: string) => `Quantity for ${part}`,
    askDue: "Requested delivery date",
    unread: (n: string) => `We could not read the attachment ${n}; could you send its contents as text or a drawing?`,
    failed: (n: string) => `The file ${n} could not be opened; could you re-send it?`,
    closing: "Kind regards,",
  },
  tr: {
    customer: "Müşteri adı",
    email: "Müşteri e-postası",
    due: "Termin",
    lines: "Kalem",
    fromMail: "mailden",
    fromFile: (n: string) => `${n} dosyasından`,
    fromDrawing: (n: string) => (n.trim() && n.trim() !== "drawing" ? `${n.trim()} çiziminden` : "çizimden"),
    entered: "siz girdiniz",
    none: "bulunamadı",
    assumed: "varsayıldı",
    subject: (no: string) => `${no} - netleştirme`,
    greeting: (name: string | null) => (name ? `Sayın ${name},` : "Merhaba,"),
    intro: "Talebiniz için teşekkürler. Doğru bir teklif hazırlayabilmemiz için birkaç bilgiye ihtiyacımız var:",
    qty: (part: string) => `${part} için adet`,
    askDue: "İstenen teslim tarihi",
    unread: (n: string) => `${n} ekini okuyamadık; içeriğini metin veya çizim olarak gönderebilir misiniz?`,
    failed: (n: string) => `${n} dosyası açılamadı; tekrar gönderebilir misiniz?`,
    closing: "Saygılarımızla,",
  },
} as const;

function sourcesOf(rfq: RfqRecord): IntakeSource[] {
  return rfq.sources ?? [];
}

function mailSource(rfq: RfqRecord): IntakeSource | null {
  return sourcesOf(rfq).find((s) => s.kind === "text") ?? null;
}

function provenanceForLine(line: RfqLine, rfq: RfqRecord): Provenance {
  const linked = sourcesOf(rfq).filter((s) => (s.line_ids ?? []).includes(line.id));
  const cad = linked.find((s) => s.kind === "cad") ?? null;
  const sheet = linked.find((s) => s.kind === "spreadsheet") ?? null;
  const text = linked.find((s) => s.kind === "text") ?? null;
  if (sheet) return { kind: "file", name: sheet.original_name || "sheet" };
  if (text) return { kind: "mail" };
  if (cad) return { kind: "drawing", name: cad.original_name || "drawing" };
  if (line.source_kind === "manual") return { kind: "entered" };
  // BOM children inherit the drawing even when intake only tagged the parent.
  if (line.source_kind === "upload_job" || line.source_kind === "catalog_bom" || line.source_job_id) {
    const drawing = sourcesOf(rfq).find((s) => s.kind === "cad");
    return { kind: "drawing", name: drawing?.original_name || "drawing" };
  }
  return { kind: "none" };
}

/**
 * Quote parts the desk should check. A `kind: "bom"` parent is the STEP
 * assembly; its children are the BOM rows. Review already flattens the same
 * way — this panel used to list only the parent, so Part showed the filename.
 */
/** Stored thumb, or the job artifact the engine bakes for this line. */
export function lineThumbUrl(line: RfqLine): string | null {
  const stored = (line.thumbnail_url || "").trim();
  if (stored) return stored;
  const job = (line.source_job_id || "").trim();
  if (!job) return null;
  if (line.source_item_no != null) {
    return `/v1/jobs/${encodeURIComponent(job)}/artifacts/thumbnail_part_${line.source_item_no}`;
  }
  return `/v1/jobs/${encodeURIComponent(job)}/artifacts/thumbnail`;
}

export function flattenDemandLines(lines: RfqLine[]): RfqLine[] {
  const out: RfqLine[] = [];
  for (const line of lines) {
    if (line.kind === "bom" && line.children?.length) {
      out.push(...flattenDemandLines(line.children));
      continue;
    }
    out.push(line);
  }
  return out;
}

export function findRfqLine(lines: RfqLine[] | undefined, id: string): RfqLine | undefined {
  for (const line of lines ?? []) {
    if (line.id === id) return line;
    const nested = findRfqLine(line.children, id);
    if (nested) return nested;
  }
  return undefined;
}

export type JobBomPart = {
  item_no: number;
  part_no: string;
  description?: string;
  qty: number;
};

function findRfqLineByItemNo(lines: RfqLine[] | undefined, itemNo: number): RfqLine | undefined {
  for (const line of lines ?? []) {
    if (line.source_item_no != null && Number(line.source_item_no) === itemNo) return line;
    const nested = findRfqLineByItemNo(line.children, itemNo);
    if (nested) return nested;
  }
  return undefined;
}

function asRow(line: RfqLine, rfq: RfqRecord): LineRow {
  return {
    line,
    qtyKnown: Number(line.qty) > 0,
    provenance: provenanceForLine(line, rfq),
  };
}

/**
 * When attach stored the STEP assembly as a single line but the parsed job
 * already has a BOM, show those parts instead of the filename.
 */
function expandWithJobBom(rfq: RfqRecord, rows: LineRow[], jobParts?: JobBomPart[]): LineRow[] {
  if (!jobParts || jobParts.length < 2 || rows.length > 1) return rows;
  const only = rows[0]?.line;
  const onlyName = (only?.part_no || "").trim().toLowerCase();
  const jobNames = new Set(jobParts.map((p) => p.part_no.trim().toLowerCase()).filter(Boolean));
  if (onlyName && jobNames.has(onlyName) && jobNames.size === 1) return rows;
  return jobParts.map((item) => {
    const existing = findRfqLineByItemNo(rfq.lines, item.item_no);
    if (existing) return asRow(existing, rfq);
    const partNo = item.part_no.trim() || `Item ${item.item_no}`;
    return {
      line: {
        id: `${only?.id ?? "job"}::item:${item.item_no}`,
        line_no: item.item_no,
        kind: "part",
        source_kind: only?.source_kind ?? "upload_job",
        part_no: partNo,
        description: (item.description || "").trim() || partNo,
        qty: item.qty,
        make_buy: "undecided",
        route_json: [],
        decision_status: "pending",
        source_job_id: only?.source_job_id,
        source_item_no: item.item_no,
      },
      qtyKnown: Number(item.qty) > 0,
      provenance: rows[0]?.provenance ?? { kind: "drawing", name: "drawing" },
    };
  });
}

export function lineRows(rfq: RfqRecord, jobParts?: JobBomPart[]): LineRow[] {
  const rows = flattenDemandLines(rfq.lines ?? []).map((line) => asRow(line, rfq));
  return expandWithJobBom(rfq, rows, jobParts);
}

export function headerRows(rfq: RfqRecord, jobParts?: JobBomPart[]): HeaderRow[] {
  const mail = mailSource(rfq);
  const lines = lineRows(rfq, jobParts).map((r) => r.line);
  return [
    {
      key: "customer",
      value: rfq.customer_name || null,
      provenance: rfq.customer_name ? { kind: "entered" } : { kind: "none" },
    },
    {
      key: "email",
      value: rfq.customer_email || null,
      provenance: rfq.customer_email ? { kind: "entered" } : { kind: "none" },
    },
    {
      key: "due",
      value: rfq.due_at ? rfq.due_at.slice(0, 10) : null,
      provenance: rfq.due_at ? (mail ? { kind: "mail" } : { kind: "entered" }) : { kind: "none" },
    },
    {
      key: "lines",
      value: lines.length ? String(lines.length) : null,
      provenance: lines.length
        ? sourcesOf(rfq).some((s) => s.kind === "cad") || lines.some((l) => l.source_job_id)
          ? {
              kind: "drawing",
              name: sourcesOf(rfq).find((s) => s.kind === "cad")?.original_name || "",
            }
          : mail
            ? { kind: "mail" }
            : { kind: "entered" }
        : { kind: "none" },
    },
  ];
}

export function rowLabel(key: HeaderRow["key"], locale: Locale): string {
  return COPY[locale][key];
}

export function provenanceLabel(p: Provenance, locale: Locale): string {
  const c = COPY[locale];
  switch (p.kind) {
    case "mail":
      return c.fromMail;
    case "file":
      return c.fromFile(p.name);
    case "drawing":
      return c.fromDrawing(p.name);
    case "entered":
      return c.entered;
    default:
      return c.none;
  }
}

/**
 * The engine attaches findings to an intake; an RFQ opened from a job (or by
 * hand) carries none, yet the same gaps are visible from the record. These
 * are derived here so the panel never says "nothing is missing" over an
 * unread quantity.
 */
function derivedGaps(rfq: RfqRecord): IntakeFinding[] {
  const out: IntakeFinding[] = [];
  for (const line of flattenDemandLines(rfq.lines ?? [])) {
    if (!(Number(line.qty) > 0)) {
      out.push({
        code: "qty_missing",
        kind: "missing",
        severity: "blocker",
        field: "qty",
        line_id: line.id,
        message: `Kalem ${line.line_no} (${line.part_no || "?"}): adet okunamadı`,
      });
    }
  }
  if (!rfq.due_at) {
    out.push({ code: "due_at_missing", kind: "missing", severity: "blocker", field: "due_at", message: "Teslim tarihi yok" });
  }
  if (!(rfq.customer_name || "").trim()) {
    out.push({
      code: "customer_name_missing",
      kind: "missing",
      severity: "warning",
      field: "customer",
      message: "Müşteri adı yok",
    });
  }
  if (!(rfq.customer_email || "").trim()) {
    out.push({
      code: "customer_email_missing",
      kind: "missing",
      severity: "warning",
      field: "customer_email",
      message: "Müşteri e-postası yok; soru ve teklif gönderilemez",
    });
  }
  return out;
}

export function effectiveFindings(rfq: RfqRecord): IntakeFinding[] {
  const derived = derivedGaps(rfq);
  const engine = rfq.findings ?? [];
  if (!engine.length) return derived;
  const codes = new Set(engine.map((f) => f.code));
  return [...engine, ...derived.filter((d) => !codes.has(d.code))];
}

type FindingLine = {
  line_no?: number | null;
  part_no?: string | null;
  qty?: number | null;
  make_buy?: string | null;
  est_cost_try?: number | null;
};

type FindingLike = {
  code: string;
  message: string;
  kind?: string | null;
  evidence?: Record<string, unknown> | null;
};

function findingCtx(finding: FindingLike, line?: FindingLine | null) {
  const evidence = finding.evidence ?? {};
  const num = (key: string): number | null => {
    const value = evidence[key];
    return typeof value === "number" && Number.isFinite(value) ? value : null;
  };
  const sku = evidence.sku != null ? String(evidence.sku) : "";
  return {
    no: line?.line_no ?? "?",
    part: (line?.part_no || "").trim() || "?",
    qty: line?.qty ?? null,
    makeBuy: line?.make_buy || "",
    cost: line?.est_cost_try ?? null,
    lead: num("est_lead_days"),
    materialLead: num("material_lead_days"),
    sku,
  };
}

type FindingVars = ReturnType<typeof findingCtx>;

const FINDING_EN: Record<string, (v: FindingVars) => string> = {
  qty_missing: (v) => `Line ${v.no} (${v.part}): quantity could not be read`,
  due_at_missing: () => "No delivery date",
  customer_name_missing: () => "No customer name",
  customer_email_missing: () => "No customer email; the question and the quote cannot be sent",
  material_uncertain: (v) => `Line ${v.no} (${v.part}): material is uncertain`,
  file_unmapped: (v) => `Line ${v.no}: no file-to-line match`,
  geometry_uncertain: (v) => `Line ${v.no}: geometry methods disagree`,
  identity_uncertain: (v) => `Line ${v.no}: title block and BOM identity disagree`,
  substitute_needs_ok: (v) => (v.sku ? `Substitute needs approval: ${v.sku}` : "Substitute needs approval"),
  lead_lt_material: (v) => `Line ${v.no}: lead ${v.lead ?? "?"} days, material supply ${v.materialLead ?? "?"} days`,
  material_lead_unread: (v) => `Line ${v.no}: material lead time could not be read`,
  setup_times_qty: (v) => `Line ${v.no}: setup counted on every piece (qty=${v.qty ?? "?"})`,
  buy_without_supplier_quote: (v) => `Line ${v.no}: ${v.makeBuy || "buy"} line has no supplier price or lead`,
  price_with_uncertain_material: (v) =>
    `Line ${v.no}: a firm price${v.cost != null ? ` (${v.cost})` : ""} while the material is uncertain`,
  process_missing: () => "No operation selected",
};

const FINDING_TR: Record<string, (v: FindingVars) => string> = {
  qty_missing: (v) => `Kalem ${v.no} (${v.part}): adet okunamadı`,
  due_at_missing: () => "Teslim tarihi yok",
  customer_name_missing: () => "Müşteri adı yok",
  customer_email_missing: () => "Müşteri e-postası yok; soru ve teklif gönderilemez",
  material_uncertain: (v) => `Kalem ${v.no} (${v.part}): malzeme belirsiz`,
  file_unmapped: (v) => `Kalem ${v.no}: dosya-kalem eşleşmesi yok`,
  geometry_uncertain: (v) => `Kalem ${v.no}: geometri yöntemleri çelişiyor`,
  identity_uncertain: (v) => `Kalem ${v.no}: başlık bloğu / BOM kimliği çelişiyor`,
  substitute_needs_ok: (v) => (v.sku ? `İkame onayı: ${v.sku}` : "İkame onayı gerekli"),
  lead_lt_material: (v) => `Kalem ${v.no}: teslim ${v.lead ?? "?"} gün, malzeme temini ${v.materialLead ?? "?"} gün`,
  material_lead_unread: (v) => `Kalem ${v.no}: malzeme temin süresi okunamadı`,
  setup_times_qty: (v) => `Kalem ${v.no}: kurulum her parçada sayılmış (adet=${v.qty ?? "?"})`,
  buy_without_supplier_quote: (v) => `Kalem ${v.no}: ${v.makeBuy || "buy"} satırında tedarikçi fiyat/termin yok`,
  price_with_uncertain_material: (v) =>
    `Kalem ${v.no}: malzeme belirsizken kesin fiyat${v.cost != null ? ` (${v.cost})` : ""}`,
  process_missing: () => "İşlem seçilmedi",
};

const KIND_EN: Record<string, string> = {
  missing: "Missing",
  uncertain: "Uncertain",
  unread: "Unread",
  failed: "Failed",
  conflict: "Conflict",
  needs_customer_ok: "Needs approval",
};

const KIND_TR: Record<string, string> = {
  missing: "Eksik",
  uncertain: "Belirsiz",
  unread: "Okunamadı",
  failed: "Hata",
  conflict: "Çelişki",
  needs_customer_ok: "Onay gerekli",
};

/**
 * Missing-data text for an RFQ finding. Known engine codes are mapped here
 * because `message` is Turkish only. An unknown code keeps the engine text
 * unless that text is Turkish and the UI is English — then the code is shown
 * rather than a sentence we cannot translate.
 */
export function findingMessage(finding: FindingLike, locale: Locale, line?: FindingLine | null): string {
  const table = locale === "tr" ? FINDING_TR : FINDING_EN;
  const render = table[finding.code];
  if (render) return render(findingCtx(finding, line));
  if (locale === "en" && /[çğıöşüÇĞİÖŞÜ]/.test(finding.message)) return finding.code;
  return finding.message;
}

export function findingKindLabel(kind: string | null | undefined, locale: Locale): string {
  if (!kind) return "";
  const table = locale === "tr" ? KIND_TR : KIND_EN;
  return table[kind] ?? kind;
}

export function splitFindings(findings: IntakeFinding[] | undefined): {
  blockers: IntakeFinding[];
  warnings: IntakeFinding[];
} {
  const all = findings ?? [];
  return {
    blockers: all.filter((f) => f.severity === "blocker"),
    warnings: all.filter((f) => f.severity !== "blocker"),
  };
}

/** True while the desk is still reading the request; after that the panel folds. */
export function isUnderstandingStage(status: RfqRecord["status"]): boolean {
  return status === "draft" || status === "estimating" || status === "waiting_customer";
}

/**
 * A question mail from the findings. Only things the customer can answer are
 * asked: quantities, the due date, unreadable attachments. Internal gaps such as
 * a missing process stay out of it.
 */
export function draftCustomerQuestion(rfq: RfqRecord, locale: Locale): { subject: string; body: string } {
  const c = COPY[locale];
  const lines = rfq.lines ?? [];
  const sources = sourcesOf(rfq);
  const asks: string[] = [];
  const seen = new Set<string>();
  const add = (s: string) => {
    if (!seen.has(s)) {
      seen.add(s);
      asks.push(s);
    }
  };
  for (const f of effectiveFindings(rfq)) {
    if (f.field === "qty" && f.line_id) {
      const line = lines.find((l) => l.id === f.line_id);
      add(c.qty(line?.part_no || line?.description || `#${line?.line_no ?? "?"}`));
    } else if (f.field === "due_at") {
      add(c.askDue);
    } else if (f.kind === "unread" || f.kind === "failed") {
      const src = sources.find((s) => s.id === f.source_id);
      const name = src?.original_name || f.message;
      add(f.kind === "failed" ? c.failed(name) : c.unread(name));
    }
  }
  // Unread attachments are worth asking about even without a finding row.
  for (const s of sources) {
    if (s.read_state === "unread" && s.kind !== "text") add(c.unread(s.original_name || s.kind));
  }
  const body = [
    c.greeting(rfq.customer_name || null),
    "",
    c.intro,
    "",
    ...asks.map((a) => `- ${a}`),
    "",
    c.closing,
  ].join("\n");
  return { subject: c.subject(rfq.rfq_no), body };
}
